/**
 * Table — DGUI component for tabular data (holdings, transactions, allocations).
 * Presentational only; schema-driven from agent/backend.
 */

import {
  Card,
  CardContent,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { TableDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  mt: 1,
  mb: 1,
  overflow: 'hidden',
};

const variantSpacing = {
  default: { py: 1.5, px: 2 },
  compact: { py: 1, px: 1.5 },
} as const;

export function Table({
  title,
  subtitle,
  columns,
  rows,
  variant = 'default',
  maxRows,
}: TableDGUIProps) {
  const spacingKey = variant === 'striped' ? 'default' : (variant ?? 'default');
  const spacing = variantSpacing[spacingKey] ?? variantSpacing.default;
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const displayRows = maxRows != null ? safeRows.slice(0, maxRows) : safeRows;
  const isEmpty = safeColumns.length === 0 || safeRows.length === 0;

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ ...spacing, '&:last-child': { pb: spacing.py } }}>
        {title && (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="#04377b"
            gutterBottom
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        ) : (
          <TableContainer>
            <MuiTable size={variant === 'compact' ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {safeColumns.map((col) => (
                    <TableCell
                      key={col.id}
                      variant="head"
                      sx={{ fontWeight: 600, color: '#04377b' }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((row, rowIdx) => (
                  <TableRow
                    key={rowIdx}
                    sx={
                      variant === 'striped' && rowIdx % 2 === 1
                        ? { backgroundColor: 'rgba(0,0,0,0.04)' }
                        : undefined
                    }
                  >
                    {safeColumns.map((col) => (
                      <TableCell key={col.id} variant="body">
                        {row[col.id] != null ? String(row[col.id]) : '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
